import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layout, RefreshCw, Share2, TrendingUp, Star, Calendar, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import SEO from '../components/SEO';
import { seoData } from '../data/seo';
import { services, projects, testimonials } from '../data/mock';
import { blogPosts } from '../data/blog';

const Home = () => {
  const iconMap = {
    Layout: Layout,
    RefreshCw: RefreshCw,
    Share2: Share2,
    TrendingUp: TrendingUp
  };

  // Get the latest blog post
  const latestPost = blogPosts[0];

  return (
    <div className="min-h-screen">
      <SEO {...seoData.home} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              We Build High-Converting Websites for{' '}
              <span className="text-cyan-500">Modern Businesses</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              We help startups, small businesses, and online brands grow through conversion-focused design, SEO, and digital strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="hero-cta-primary">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-8 py-6 text-lg">
                  Start Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/portfolio" data-testid="hero-cta-secondary">
                <Button variant="outline" className="border-2 border-gray-300 hover:border-cyan-500 rounded-full px-8 py-6 text-lg">
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Statistics Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mb-8">
            <div>
              <p className="text-4xl font-bold text-cyan-500 mb-2">50+</p>
              <p className="text-gray-900 font-medium">Projects Completed</p>
              <p className="text-sm text-gray-500 mt-1">Websites, landing pages, and digital solutions delivered to satisfied clients worldwide</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-cyan-500 mb-2">100%</p>
              <p className="text-gray-900 font-medium">Client Satisfaction</p>
              <p className="text-sm text-gray-500 mt-1">We pride ourselves on delivering exceptional results that exceed expectations every time</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-cyan-500 mb-2">24/7</p>
              <p className="text-gray-900 font-medium">Support Available</p>
              <p className="text-sm text-gray-500 mt-1">Our dedicated team is always ready to assist you with any questions or concerns</p>
            </div>
          </div>
          <p className="text-center text-gray-600 font-medium">
            Trusted by startups, e-commerce brands, and service businesses worldwide.
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              What We Do
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive digital services to elevate your online presence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Link 
                  key={service.id} 
                  to={`/services#${service.slug}`}
                  className="block"
                  data-testid={`service-card-${service.slug}`}
                >
                  <Card className="p-6 h-full hover:shadow-lg transition-all duration-300 border-2 hover:border-cyan-500 rounded-lg cursor-pointer">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-cyan-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </Card>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-gray-300 hover:border-cyan-500 rounded-full px-8">
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Recent Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Check out some of our latest work
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.slice(0, 4).map((project) => (
              <Card key={project.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 rounded-lg group">
                <div className="h-64 bg-gray-100 overflow-hidden">
                  <img
                    src={project.image}
                    alt={`${project.name} - ${project.category} project by Web Growth Studio`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                    {project.name}
                  </h3>
                  <p className="text-gray-600 mb-2">{project.description}</p>
                  <p className="text-sm text-cyan-600 italic mb-4">{project.result}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-cyan-500 hover:text-cyan-600 font-medium"
                  >
                    Visit Website
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/portfolio">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-8">
                View Full Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Insights / Blog Section */}
      {latestPost && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Latest Insights
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Tips and strategies to help your business grow online
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Link to={`/blog/${latestPost.slug}`} className="block group">
                <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="h-64 md:h-auto bg-gray-100 overflow-hidden">
                      <img
                        src={latestPost.image}
                        alt={latestPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(latestPost.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {latestPost.readTime}
                        </span>
                      </div>
                      <span className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full mb-3 w-fit">
                        {latestPost.category}
                      </span>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-cyan-500 transition-colors">
                        {latestPost.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {latestPost.excerpt}
                      </p>
                      <span className="inline-flex items-center text-cyan-500 font-medium group-hover:gap-2 transition-all">
                        Read Article
                        <ArrowRight className="ml-1 w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>

            <div className="text-center mt-8">
              <Link to="/blog">
                <Button variant="outline" className="border-2 border-gray-300 hover:border-cyan-500 rounded-full px-8">
                  View All Articles
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              What Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-6 rounded-lg bg-white">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We follow a proven methodology to deliver exceptional results for every project
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-2">Discovery</h3>
              <p className="text-gray-600">We learn about your business goals, target audience, and project requirements through detailed consultation sessions.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-2">Design</h3>
              <p className="text-gray-600">Our creative team develops custom designs that reflect your brand identity and engage your target audience effectively.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-2">Development</h3>
              <p className="text-gray-600">We build your website using modern technologies ensuring fast loading speeds, mobile responsiveness, and SEO optimization.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-xl font-semibold mb-2">Launch</h3>
              <p className="text-gray-600">After thorough testing and your approval, we launch your project and provide ongoing support to ensure continued success.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's create something amazing together. Get in touch today for a free consultation and discover how we can transform your online presence.
          </p>
          <Link to="/contact" data-testid="cta-start-project">
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-8 py-6 text-lg">
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
